import React from 'react'
import {
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    makeStyles,
} from '@material-ui/core'
import dateFormat from 'date-fns/format'

import Widget from '../../components/Widget'
import { Button, Typography } from '../../components/Wrappers'
import { useMyAppointments } from '../../apis'
import { useUserState } from '../../context/UserContext'
import {
    formatAppointmentTimeRange,
    formatAppointmentTypeName,
    formatHouseholdName,
    toLocalDate,
} from './calendarHelpers'

/**
 * DashboardAppointments - the handler's upcoming appointments section on the
 * dashboard (Requirements 12.1 - 12.6).
 *
 * WHERE IT IS RENDERED: /app/dashboard is `CallLogs` with `isDashboard`
 * (see Layout.js), so that is the dashboard this section is mounted in - NOT
 * pages/dashboard/Dashboard.js, which is unrouted template code fed by mock.js.
 *
 * THE SECTION OWNS ITS OWN VISIBILITY (Requirement 12.4). It returns null for a
 * non handler rather than making the dashboard test the role, so the dashboard
 * only has to render <DashboardAppointments /> and this file stays the single
 * place the rule lives.
 *
 * HOW "IS A HANDLER" IS DECIDED: by ROLE NAME on the session user.
 * GET /users/current returns ctx.state.user, whose `roles` relation is eager
 * loaded (koa/middleware/authenticate.js -> withGraphFetched('roles')), so each
 * entry is a full role row including `name`; Sidebar.js already gates menu items
 * on user.roles[].name the same way. The comparison is case insensitive to match
 * the backend, which resolves this role with `lower(roles.name)` rather than an
 * id - THE SEED COMPUTES THE ROLE ID AT INSERT TIME (max + 1), so the id differs
 * per environment and must never be hard coded here.
 *
 * WHY NOT DERIVE IT FROM THE DATA: "render only when the list is non empty" is
 * the one thing that cannot work - Requirement 12.5 says a handler with zero
 * upcoming appointments still gets the section, with a message. And gating on
 * GET /appointments/handlers (the active-handlers picker) would hide the section
 * from a user whose handler role was removed but who still has appointments they
 * are on the hook for (Requirement 3.5) - the role on the session user is the
 * same fact without that hole.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: filter, sort or slice. The endpoint already
 * returns only appointments after the current moment, ordered soonest first and
 * capped at 50 (Requirements 12.1 / 12.2, enforced in getMyAppointments), and
 * re-doing any of it on the client would only be a second, drifting definition of
 * "upcoming".
 */

//the seeded role name; compared case insensitively, like the backend does
export const HANDLER_ROLE_NAME = 'appointment handler'

export const isAppointmentHandler = user =>
    !!user &&
    Array.isArray(user.roles) &&
    user.roles.some(role => !!role && String(role.name || '').trim().toLowerCase() === HANDLER_ROLE_NAME)

//small and local: the shared appointments stylesheet is the calendar's, and this
//section is a plain table that needs nothing from it
const useStyles = makeStyles(theme => ({
    message: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: theme.spacing(2),
    },
    retry: {
        marginLeft: theme.spacing(1),
    },
    nowrap: {
        whiteSpace: 'nowrap',
    },
}))

//'Mon, Feb 16, 2026' - built from the y/m/d parts by toLocalDate, never by
//parsing the ISO string a mysql DATE becomes over JSON (that shifts a day back
//in every US timezone and would show the wrong date here)
const formatAppointmentDate = value => {
    const localDate = toLocalDate(value)
    return localDate ? dateFormat(localDate, 'EEE, MMM d, yyyy') : ''
}

const DashboardAppointments = () => {
    const classes = useStyles()
    const { user } = useUserState()
    const isHandler = isAppointmentHandler(user)

    //Requirement 12.4 - not a handler, no section at all. The hook is still
    //called (hooks cannot be conditional), but SWR de-dupes it and the endpoint
    //is open to any authenticated user, so nothing is wasted beyond one request.
    const { appointments, isLoading, isError, mutate } = useMyAppointments()

    if (!isHandler) return null

    return (
        <Widget title="My Upcoming Appointments" disableWidgetMenu noBodyPadding>
            {/* Requirement 12.6 - the loading indicator sits above the list rather
                than replacing it, so a revalidation does not blank the section */}
            {!!isLoading && <LinearProgress aria-label="Loading upcoming appointments" />}

            {!!isError && (
                <div className={classes.message} role="alert">
                    <Typography variant="body2" color="secondary">
                        Your upcoming appointments could not be retrieved.
                    </Typography>
                    <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => mutate()}
                        className={classes.retry}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {/* Requirement 12.5 - a handler with nothing coming up gets the section
                and a message, not an empty box. Held back while the first load is
                still running so it does not flash before the rows arrive. */}
            {!isError && !isLoading && !appointments.length && (
                <div className={classes.message}>
                    <Typography variant="body2" color="text" colorBrightness="secondary">
                        No upcoming appointments are scheduled.
                    </Typography>
                </div>
            )}

            {/* Requirement 12.3 - date, time, type and household name. Rows are
                rendered in the order the api sent them (Requirement 12.2). */}
            {!!appointments.length && (
                <Table size="small" aria-label="My upcoming appointments">
                    <TableHead>
                        <TableRow>
                            <TableCell component="th" scope="col">
                                Date
                            </TableCell>
                            <TableCell component="th" scope="col">
                                Time
                            </TableCell>
                            <TableCell component="th" scope="col">
                                Type
                            </TableCell>
                            <TableCell component="th" scope="col">
                                Household
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {appointments.map(appointment => (
                            <TableRow key={appointment.id} hover>
                                <TableCell component="th" scope="row" className={classes.nowrap}>
                                    {formatAppointmentDate(appointment.date)}
                                </TableCell>
                                <TableCell className={classes.nowrap}>
                                    {formatAppointmentTimeRange(appointment)}
                                </TableCell>
                                {/* this endpoint returns the appointmentType and
                                    household relations only - the handler is the
                                    logged in user - and a trimmed relation can still
                                    be missing, so both fall back rather than blank */}
                                <TableCell>{formatAppointmentTypeName(appointment) || 'Appointment'}</TableCell>
                                <TableCell>
                                    {formatHouseholdName(appointment.household) ||
                                        `Household #${appointment.householdId}`}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Widget>
    )
}

export default DashboardAppointments
